import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { DataTable } from "src/features/reporting/components";
import { useStoreReport } from "src/features/reporting/hooks";
import { checkObservationFormType } from "src/utils/checkObservationFormType";

interface Props {
	formId: number;
	year: number;
	areaId: number;
	storeId?: number;
	showCompliantReport?: boolean;
	showObservationsPassedThresholdReport?: boolean;
	observationType: IObservationType;
	calendarMonth?: Month;
	calendarYear?: number;
	onDownloadReady?: (downloadFn: () => void) => void;
}

const MonthlyStoreReport: FC<Props> = ({
	formId,
	year,
	areaId,
	storeId,
	showCompliantReport,
	showObservationsPassedThresholdReport,
	observationType,
	calendarMonth,
	calendarYear,
	onDownloadReady
}) => {
	const { setValue } = useFormContext<ReportDetailForm>();

	const { columns, data, isSuccess, isLoading, download } = useStoreReport({
		year,
		form: formId,
		compliancy: showCompliantReport,
		threshold: showObservationsPassedThresholdReport,
		areaId,
		observationType,
		setValue
	});

	useEffect(() => {
		if (download && onDownloadReady) {
			onDownloadReady(download);
		}
	}, [download, onDownloadReady]);

	// When a single store is selected (e.g. from the store search) scope the
	// store-by-month grid to that store's row, so the search result is shown
	// in the table within the chosen region/area/store filters.
	const allRows = data ?? [];
	const rows = storeId
		? allRows.filter((row) => row.storeId === storeId)
		: allRows;

	return (
		<>
			{isLoading && (
				<div className="flex justify-center items-center py-12">
					<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
				</div>
			)}
			{isSuccess && (
				<DataTable
					columns={columns}
					data={rows}
					calendarMonth={calendarMonth}
					calendarYear={calendarYear}
					observationType={observationType}
					emptyDataMessage="No store data found"
					onRowClick={
						checkObservationFormType(observationType) === "user"
							? (row: any) => setValue("filters.storeId", row.storeId)
							: undefined
					}
				/>
			)}
		</>
	);
};

export default MonthlyStoreReport;
